<template>
  <form @submit.prevent="saveAllLinks">
    <div>
      <div class="px-3 pt-3 pb-1 mb-3 border rounded-bottom tab-container">
        <div class="pre-wrap d-flex justify-content-between">
          {{ $t("dashboard-settings.external-links-message") }}
          <button
            type="button"
            class="btn btn-warning btn-wide"
            v-if="isSecretUser"
            @click.prevent="showAddModal"
            :disabled="!isSecretUser"
          >
            <i class="fas fa-plus mr-1"></i>
            {{ $t("dashboard-settings.button-add") }}
          </button>
        </div>

        <table class="table table-bordered table-sm hoverable mt-3" id="data-table">
          <thead>
            <tr>
              <th class="links-text">{{ $t("dashboard-settings.external-links-text") }}</th>
              <th class="links-image">{{ $t("dashboard-settings.external-links-image") }}</th>
              <th class="links-url">{{ $t("dashboard-settings.external-links-url") }}</th>
              <th class="links-display">{{ $t("dashboard-settings.external-links-display") }}</th>
              <th class="links-edit">{{ $t("dashboard-settings.external-links-edit") }}</th>
            </tr>
          </thead>
          <draggable v-model="facilityLinks" tag="tbody" handle=".handle" :disabled="!isSecretUser">
            <tr v-for="item in sortedLinks" :key="item.display_order">
              <td>
                <span class="fas fa-grip-vertical handle"></span>
                <span style="padding-left: 8px;">{{ item.text }}</span>
              </td>
              <td>
                <div class="image-container">
                  <!-- デフォルトアイコン -->
                  <div v-if="!item.label.includes('option')">
                    <img :src="item.imagePath" alt="Image" class="img-default-preview" />
                  </div>
                  <!-- オプションアイコン -->
                  <div :class="{'image-container': true, 'overflow-hidden': item.image}" v-if="item.image">
                    <img :src="item.image" alt="Image" class="img-preview" />
                  </div>
                </div>
              </td>
              <td>
                <a :href="item.url" target="_blank">{{ item.url }}</a>
              </td>
              <td>
                <toggle-button
                  :width="70"
                  :labels="{ checked: 'ON', unchecked: 'OFF' }"
                  :value="item.visibility === 1"
                  :color="{ checked: $theme.purple, unchecked: $theme.red }"
                  :disabled="!isSecretUser"
                  @change="updateVisibility(item)"
                />
              </td>
              <td>
                <button class="btn btn-primary btn-round" @click.prevent="showEditModal(item)" :disabled="!isSecretUser">
                  <i class="fas fa-edit"></i>
                </button>
              </td>
            </tr>
          </draggable>
        </table>

        <!-- 追加モーダル -->
        <b-modal v-model="isAddModalVisibility" :title="$t('dashboard-settings.external-links-add-modal')" @hide="resetForm" hide-footer>
            <form @submit.prevent="saveLink">
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="text">{{ $t("dashboard-settings.external-links-text") }}</label>
                <div class="col-sm-8">
                  <input
                    type="text"
                    v-model="newLink.text"
                    class="form-control"
                    required
                    @input="validateForm(newLink)"
                  />
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="url">{{ $t("dashboard-settings.external-links-url") }}</label>
                <div class="col-sm-8">
                  <input
                    type="url"
                    v-model="newLink.url"
                    class="form-control"
                    required
                    @input="validateForm(newLink)"
                  />
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="image">{{ $t("dashboard-settings.external-links-image") }}</label>
                <div class="col-sm-8">
                  <b-button
                    variant="outline-primary"
                    class="btn-min mr-2"
                    @click="$refs.file_upload.click()"
                  >
                    <i class="fa fa-upload"></i>
                    {{ $t("common.word-upload") }}
                  </b-button>
                  <input
                    type="file"
                    accept="image/*"
                    ref="file_upload"
                    style="display: none"
                    @change="onImageChange"
                  />
                </div>
              </div>
              <div class="form-group row" v-if="previewImage">
                <label class="col-sm-4 col-form-label">{{ $t("dashboard-settings.external-links-preview") }}</label>
                <div class="col-sm-8">
                  <div class="image-container overflow-hidden">
                    <img :src="previewImage" alt="Image Preview" class="img-preview" />
                  </div>
                </div>
              </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-warning btn-min ml-3 text-white" >
              <i class="fas fa-check mr-1"></i>
              {{ $t("dashboard-settings.button-save") }}
            </button>
          </div>
          </form>
        </b-modal>

        <!-- 編集モーダル -->
        <b-modal v-model="isEditModalVisibility" :title="$t('dashboard-settings.external-links-edit-modal')" @hide="resetEditForm" hide-footer>
            <form @submit.prevent="updateLink">
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="text">{{ $t("dashboard-settings.external-links-text") }}</label>
                <div class="col-sm-8">
                  <input
                    type="text"
                    v-model="editLink.text"
                    class="form-control"
                    required
                    @input="validateForm(editLink)"
                  />
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="url">{{ $t("dashboard-settings.external-links-url") }}</label>
                <div class="col-sm-8">
                  <input
                    v-model="editLink.url"
                    type="url"
                    class="form-control"
                    required
                    @input="validateForm(editLink)"
                  />
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-4 col-form-label" for="image">{{ $t("dashboard-settings.external-links-image") }}</label>
                <div class="col-sm-8">
                  <b-button
                    variant="outline-primary"
                    class="btn-min mr-2"
                    :disabled="!isEditEnabled"
                    @click="$refs.file_upload_edit.click()"
                  >
                    <i class="fa fa-upload"></i>
                    {{ $t("common.word-upload") }}
                  </b-button>
                  <input
                    type="file"
                    accept="image/*"
                    ref="file_upload_edit"
                    style="display: none"
                    @change="onEditImageChange"
                  />
                </div>
              </div>
              <div class="form-group row" v-if="previewEditImage">
                <label class="col-sm-4 col-form-label">{{ $t("dashboard-settings.external-links-preview") }}</label>
                <div class="col-sm-8">
                  <div class="image-container overflow-hidden">
                    <img :src="previewEditImage" alt="Edit Image Preview" class="img-preview" />
                  </div>
                </div>
              </div>
            <div class="modal-footer">
            <button type="submit" class="btn btn-warning btn-min ml-3 text-white">
              <i class="fas fa-check mr-1"></i>
              {{ $t("dashboard-settings.button-save") }}
            </button>
            </div>
          </form>
        </b-modal>
      </div>

      <!-- 保存ボタン -->
      <div class="row mt-3" v-if="isSecretUser">
        <div class="col-lg-12 text-right">
          <button
            type="submit"
            class="btn btn-warning btn-wide mx-1"
          >
            <i class="fas fa-save mr-1"></i>
            {{ $t("dashboard-settings.button-save") }}
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import draggable from 'vuedraggable';
import { mapActions, mapGetters } from "vuex";
import uac from "@/mixins/uac";
import iconApi from "@/api/icon";
import imageCompression from 'browser-image-compression';

export default {
  components: {
    draggable,
  },
  computed: {
    ...mapGetters("dashboard", ["facility", 'userData']),
    sortedLinks() {
      return this.facilityLinks.map(item => ({
        ...item,
        imagePath: this.getImagePath(item.label)
      }));
    },
  },
  mixins: [uac],
  data() {
    return {
      facilityLinks: [],
      newLink: {
        facility_id: null,
        display_order: null,
        label: '',
        text: '',
        url: '',
        image: '',
        visibility: true,
      },
      editLink: {
        facility_id: null,
        display_order: null,
        label: '',
        text: '',
        url: '',
        image: '',
      },
      isAddModalVisibility: false,
      isEditModalVisibility: false,
      previewImage: '',
      previewEditImage: '',
      facility_id: null,
      isSecretUser: false,
      isEditEnabled: false
    };
  },
  beforeMount() {
    this.facility_id = this.facility.id;
    this.isSecretUser = this.userData.id == this.facility.user_id;
  },
  async mounted() {
    await this.initializeIcons(this.facility.id);
    await this.showFacilityLinks(this.$i18n.locale);
    this.facilityLinks.sort((a, b) => a.display_order - b.display_order);
  },
  methods: {
    ...mapActions("dashboard", ["updateFacilities"]),
    async initializeIcons(facility_id) {
      try {
        await iconApi.init(facility_id);
      } catch (e) {
        console.error(e.message);
        this.$toast.error(this.$t("dashboard-common.error-generic"), {
          position: "top"
        });
      }
    },
    async showFacilityLinks(lang) {
      try {
        const response = await iconApi.getIcons(this.facility.id);
        this.facilityLinks = response.filter(link => link.language === lang);
      } catch (e) {
        console.error(e.message);
        this.$toast.error(this.$t("dashboard-common.error-generic"), {
          position: "top"
        });
      }
    },

    getImagePath(label) {
      const imgContext = require.context('@/assets/img', false, /\.(svg)$/);
      const fileName = (() => {
      switch (label) {
        case 'congestion_status_large_bath': return 'onsen_even.svg';
          case 'congestion_status_laundry': return 'congestion-status-laundry_even.svg';
          case 'congestion_status_dining_hall': return 'congestion-status-dining-hall_even.svg';
          case 'intercom': return 'intercom_even.svg';
          case 'inquiry': return 'inquiry_even.svg';
          case 'wifi': return 'wifi_even.svg';
          case 'tourist_information': return 'tourist-information_even.svg';
          case 'food_information': return 'food-information_even.svg';
          case 'official_website': return 'official-website_even.svg';
          default: return '';
        }
      })();
      return fileName ? imgContext(`./${fileName}`) : "";
    },

    showAddModal() {
      this.newLink = { display_order: null, label: '',text: '', url: '', image: '', visibility: 0 };
      this.isAddModalVisibility = true;
    },
    showEditModal(item) {
      this.editLink = { ...item };
      this.previewEditImage = item.image;
      this.isEditModalVisibility = true;
      this.isEditEnabled = this.editLink.label.startsWith('option_');
    },
    resetForm() {
      this.newLink = { text: '', url: '', image: '' };
      this.previewImage = '';
    },
    resetEditForm() {
      this.editLink = { text: '', url: '', image: '' };
      this.previewEditImage = '';
    },

    validateForm(link) {
      // テキストのバリデーション
      if (!link.text.trim()) {
        return false;
      }
      // URLのバリデーション
      const urlPattern = /^(https?:\/\/)/i;
      if (!link.url || !urlPattern.test(link.url)) {
        return false;
      }
      return true;
    },

    saveLink() {
      if (!this.validateForm(this.newLink)) {
        return;
      }

      // 画像のバリデーション
      if (!this.previewImage) {
        this.$toast.error(this.$t("dashboard-settings.error.empty-image"));
        return;
      }

      //初期値の設定
      this.newLink.facility_id = this.facility_id,
      this.newLink.label = this.generateNextLabel();
      this.newLink.display_order = this.facilityLinks.length + 1;
      this.newLink.image = this.previewImage;
      this.newLink.visibility = 1;
      this.newLink.language = this.$i18n.locale;

      this.facilityLinks.push({
        ...this.newLink,
      });
      this.isAddModalVisibility = false;
      this.resetForm();
    },

    updateLink() {
      if (!this.validateForm(this.editLink)) {
        return;
      }
      // 画像のバリデーション
      if (this.editLink.label.startsWith('option_icon') && this.previewImage) {
        this.$toast.error(this.$t("dashboard-settings.error.empty-image"));
        return;
      }

      this.editLink.facility_id = this.facility_id;
      this.editLink.image = this.previewEditImage;

      const index = this.facilityLinks.findIndex(link => link.display_order === this.editLink.display_order);
      if (index !== -1) {
          this.facilityLinks[index].text = this.editLink.text;
          this.facilityLinks[index].url = this.editLink.url;
          this.facilityLinks[index].image = this.previewEditImage || this.facilityLinks[index].image;
          this.facilityLinks[index].visibility = this.editLink.visibility;
        };
      this.isEditModalVisibility = false;
      this.resetEditForm();
    },

    generateNextLabel() {
      // option_iconで始まるlabelの数をカウント
      const count = this.facilityLinks
        .filter(link => link.label.startsWith('option_icon')).length;
      const newNumber = count + 1;
      return `option_icon_${newNumber}`;
    },

    // isEdit が false の場合:リンクの新規追加 /isEdit が true の場合:既存のリンクの編集
    async handleFileChange(event, isEdit = false) {
      const file = event.target.files[0];
      if (file) {
        // 拡張子制限
        const validExtensions = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!validExtensions.includes(file.type)) {
          this.$toast.error(this.$t("dashboard-settings.error.invalid-file-type"));
          return;
        }

        // ファイルサイズチェック（2MB以上はエラー）
        const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
        if (file.size > maxSizeInBytes) {
          this.$toast.error(this.$t("dashboard-settings.error.file-size-exceeded"));
          return;
        }

        // SVGはそのまま処理（圧縮不要）
        let processedFile = file;

        if (file.type !== 'image/svg+xml') {
          // 画像圧縮オプション
          const options = {
            maxSizeMB: 1,              // 最大1MB
            maxWidthOrHeight: 600,     // 最大幅/高さ600px
            useWebWorker: false,       // CSP制限のためWeb Workerを無効化
            fileType: 'image/png'      // PNG形式に統一
          };

          try {
            // 画像を圧縮
            processedFile = await imageCompression(file, options);
          } catch (error) {
            // 圧縮失敗時は元の画像を使用
            console.error('Image compression failed, using original file:', error);
            processedFile = file;
          }
        }

        // Base64に変換
        const reader = new FileReader();
        reader.onload = (e) => {
          if (isEdit) {
            this.previewEditImage = e.target.result;
          } else {
            this.previewImage = e.target.result;
          }
        };
        reader.readAsDataURL(processedFile);
      }
    },

    onImageChange(event) {
      this.handleFileChange(event);
    },

    onEditImageChange(event) {
      this.handleFileChange(event, true);
    },

    updateVisibility(item) {
      const index = this.facilityLinks.findIndex(link => link.display_order === item.display_order);
      if (index !== -1) {
        this.facilityLinks[index].visibility = this.facilityLinks[index].visibility === 0 ? 1 : 0;
      }
    },

    async saveAllLinks() {
      try {
        const savedLinks = this.facilityLinks.map((link,index) => ({
            ...link,
            display_order: index + 1
        }));
        await iconApi.saveIcons(savedLinks);

        this.$toast.success(this.$t("dashboard-common.updated-generic"), {
          position: "top"
        });
      } catch (e) {
        console.error(e);
        this.$toast.error(this.$t("dashboard-common.error-generic"), {
          position: "top"
        });
      }
    }
  }
}
</script>

<style scoped>
.image-container {
  width: 100%;
  height: 0;
  padding-bottom: 31.6%;
  position: relative;
}

.overflow-hidden {
  overflow: hidden;
}

.img-preview {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: auto;
  transform: translate(-50%, -50%);
  object-fit: cover;
}

.img-default-preview{
  position: absolute;
  top: 50%;
  left: 50%;
  width: 42%;
  height: auto;
  transform: translate(-50%, -50%);
  object-fit: cover;
}
.table {
  table-layout: fixed; 
  width: 100%;
}
.links-text{
  width: 26%
}
.links-image{
  width: 12%
}
.links-url{
  width: 40%
}
.links-display{
  width: 80px
}

.links-edit{
  width: 50px
}
a{
  display: block; 
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
